/** A scenario type's bulk runs in the runs sheet; picking one opens it on the bulk-run page. */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { bulkRunUrl } from '@/constants/paths';
import { aiFunctionService } from '@/services/ai-function.service';
import type { BulkRun } from '@/types/websocket.types';
import { runCounts } from './run-counts';
import { RunsList, RunsSheet } from './runs-sheet';
import { BULK_RUNS_QUERY_ROOT, runIdOf, runPagesAtom, useRunPages } from './use-run-pages';

const useTypeBulkRuns = (sopId: string, typeId: string) => {
	const queryAtom = useMemo(
		() =>
			runPagesAtom([BULK_RUNS_QUERY_ROOT, sopId, typeId], (offset, limit) =>
				aiFunctionService.fetchBulkRuns(sopId, typeId, offset, limit)
			),
		[sopId, typeId]
	);
	return useRunPages(queryAtom, runIdOf, 'The bulk runs could not be loaded.');
};

/** The counts the run's summary tiles show, in one line. */
const bulkRunOutcome = (run: BulkRun): string => {
	const { settled, perfect, flagged, unjudged, failed } = runCounts(run.items);
	if (run.status === 'running') return `Ran ${settled} of ${run.items.length} so far`;
	const toReview = unjudged > 0 ? ` · ${unjudged} to review` : '';
	return `${perfect} Perfect · ${flagged} Flagged · ${failed} Failed${toReview}`;
};

interface TypeBulkRunsProps {
	sopId: string;
	typeId: string;
	currentRunId: string | null;
	onOpened: () => void;
}

const TypeBulkRuns = ({ sopId, typeId, currentRunId, onOpened }: TypeBulkRunsProps) => {
	const navigate = useNavigate();
	const pages = useTypeBulkRuns(sopId, typeId);
	return (
		<RunsList
			pages={pages}
			currentRunId={currentRunId}
			outcome={bulkRunOutcome}
			empty='No bulk runs of this scenario type yet.'
			onOpen={(run) => {
				onOpened();
				navigate(bulkRunUrl(sopId, typeId, run.runId));
			}}
		/>
	);
};

interface TypeBulkRunsSheetProps extends Omit<TypeBulkRunsProps, 'onOpened'> {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const TypeBulkRunsSheet = ({ open, onOpenChange, ...list }: TypeBulkRunsSheetProps) => (
	<RunsSheet
		open={open}
		onOpenChange={onOpenChange}
		title='Bulk runs'
		description='Every bulk run of this scenario type, newest first.'
	>
		<TypeBulkRuns {...list} onOpened={() => onOpenChange(false)} />
	</RunsSheet>
);
